import { FC } from "react";
import { useIntl } from "react-intl";

import { Box, Typography } from "@mui/material";

import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { formatMeasurementLabel } from "../../../../../../utils/measurement-label";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import { formatNyquistUnitLabel, INyquistDataMark, PHASE_UNIT_CODE } from "./model";
import { getNyquistTooltipLines, NYQUIST_TOOLTIP_COLORS } from "./tooltip-utils";

export const NyquistDataMarkTooltip: FC<{ mark: INyquistDataMark }> = ({ mark }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const formatValue = (value: number, unitCode: number | null) =>
    formatMeasurementNumber(value, unitCode, profile.interfaceSettings.dataFormat, { fallbackDigits: 2 });
  const units = (unitCode: number) => formatNyquistUnitLabel(formatMessage({ id: `enums.units.${unitCode}` }));
  const control = mark.nyquist.controlParameter;
  const lines = getNyquistTooltipLines(
    {
      pointName: mark.pointName,
      measureName: formatMeasurementLabel(mark.measureName, mark.measureDescription),
      date: mark.date,
      amplitude: mark.value,
      phaseDegrees: mark.value2,
      unitCode: mark.units,
      amplitudeUnits: units(mark.units),
      phaseUnits: units(PHASE_UNIT_CODE),
    },
    {
      point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
      measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
      dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
      amplitude: formatMessage({ id: "pages.speed.nyquist.amplitude" }),
      phase: formatMessage({ id: "pages.speed.nyquist.phase" }),
    },
    formatValue,
  );

  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 360,
        bgcolor: NYQUIST_TOOLTIP_COLORS.background,
        color: NYQUIST_TOOLTIP_COLORS.body,
        borderRadius: 1,
        overflowWrap: "anywhere",
        ".MuiTooltip-tooltip &": { m: "-4px -8px" },
      }}
    >
      {lines.map((line, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: 12,
            fontWeight: index === 0 ? 600 : 400,
            color: index === 0 ? NYQUIST_TOOLTIP_COLORS.title : "inherit",
          }}
        >
          {line}
        </Typography>
      ))}
      {control ? (
        <Typography sx={{ fontSize: 12, color: "inherit" }}>
          {control.title}: {formatValue(control.value, null)} {control.units}
        </Typography>
      ) : null}
    </Box>
  );
};
